/**
 * Office.js Email Extraction Service
 * Handles extracting email data and attachments from Outlook
 */

export interface EmailData {
  subject: string;
  bodyHtml: string;
  bodyText: string;
  senderEmail: string;
  senderName: string;
  toRecipients: string;
  ccRecipients: string;
  receivedDate: Date;
  hasAttachments: boolean;
  attachmentCount: number;
  // Attachment metadata already extracted from Office.js. Consumers should
  // read from here instead of re-accessing Office.context.mailbox.item.attachments.
  attachments: Office.AttachmentDetails[];
  outlookMessageId: string;
  // REST-format messageId required by Microsoft Graph (`/users/{upn}/messages/{id}`).
  // On Outlook desktop, item.itemId is EWS-format and must be converted via convertToRestId.
  restMessageId: string;
  userPrincipalName: string;
  conversationId: string;
  isOutgoing?: boolean;
}

export interface AttachmentData {
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  content: string; // Base64 encoded
}

/**
 * Extract email metadata from currently selected email
 */
export const extractEmailData = async (): Promise<EmailData> => {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item; 
    
    if (!item) {
      reject(new Error('No item selected'));
      return;
    }

    if (item.itemType !== Office.MailboxEnums.ItemType.Message) {
      reject(new Error('Please select an email message'));
      return;
    }

    // Read userProfile.emailAddress once — used for both the isOutgoing check
    // and userPrincipalName below.
    const userEmail = Office.context.mailbox.userProfile.emailAddress || '';

    // Extract all email properties in parallel
    Promise.all([
      getSubject(item),
      getBody(item),
      getSender(item),
      getRecipients(item),
      getReceivedDate(item),
      getAttachments(item),
      getMessageId(item),
      getConversationId(item),
    ])
      .then(([subject, body, sender, recipients, receivedDate, attachments, messageId, conversationId]) => {
        // Determine if this is an outgoing email by comparing sender with current user
        const currentUserEmail = userEmail.toLowerCase();
        const senderEmail = (sender.email || '').toLowerCase();
        const isOutgoing = senderEmail === currentUserEmail;

        resolve({
          subject,
          bodyHtml: body.html,
          bodyText: body.text,
          senderEmail: sender.email,
          senderName: sender.name,
          toRecipients: recipients.to,
          ccRecipients: recipients.cc,
          receivedDate,
          hasAttachments: attachments.length > 0,
          attachmentCount: attachments.length,
          attachments,
          outlookMessageId: messageId,
          restMessageId: getRestMessageId(messageId),
          userPrincipalName: userEmail,
          conversationId,
          isOutgoing: isOutgoing,
        });
      })
      .catch(reject);
  });
};

/**
 * Get email subject
 */
const getSubject = (item: Office.MessageRead): Promise<string> => {
  return Promise.resolve(item.subject || '(No Subject)');
};

/**
 * Get email body (HTML + plain text derived from it).
 * Single Office.js round-trip — the text variant is parsed from the HTML
 * locally instead of issuing a second getAsync.
 */
const getBody = (item: Office.MessageRead): Promise<{ html: string; text: string }> => {
  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Html, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error('Failed to get email body'));
        return;
      }
      const html = result.value || '';
      let text = '';
      try {
        text = new DOMParser().parseFromString(html, 'text/html').body?.textContent || '';
      } catch {
        text = '';
      }
      resolve({ html, text });
    });
  });
};

/**
 * Get sender information
 */
const getSender = (item: Office.MessageRead): Promise<{ email: string; name: string }> => {
  return Promise.resolve({
    email: item.sender?.emailAddress || '',
    name: item.sender?.displayName || '',
  });
};

/**
 * Get sender information for outgoing emails (use current user)
 */
const getOutgoingSender = (item: any): Promise<{ email: string; name: string }> => {
  return new Promise((resolve) => {
    // For outgoing emails, the sender is the current user
    const userProfile = Office.context.mailbox.userProfile;
    resolve({
      email: userProfile.emailAddress || '',
      name: userProfile.displayName || '',
    });
  });
};

/**
 * Get recipients (TO and CC)
 */
const getRecipients = (item: Office.MessageRead): Promise<{ to: string; cc: string }> => {
  const toRecipients = item.to?.map((r) => `${r.displayName} <${r.emailAddress}>`).join('; ') || '';
  const ccRecipients = item.cc?.map((r) => `${r.displayName} <${r.emailAddress}>`).join('; ') || '';
  
  return Promise.resolve({ to: toRecipients, cc: ccRecipients });
};

/**
 * Get received date
 */
const getReceivedDate = (item: Office.MessageRead): Promise<Date> => {
  return Promise.resolve(item.dateTimeCreated || new Date());
};

/**
 * Get attachment information
 */
const getAttachments = (item: Office.MessageRead): Promise<Office.AttachmentDetails[]> => {
  return Promise.resolve(item.attachments || []);
};

/**
 * Get message ID
 */
const getMessageId = (item: Office.MessageRead): Promise<string> => {
  return Promise.resolve(item.itemId || '');
};

/**
 * Convert an Outlook itemId to Microsoft Graph REST format.
 * On Outlook on the Web/Mac/mobile the itemId is already REST-format and
 * convertToRestId is a no-op; on Outlook desktop it's EWS format and must be
 * converted before being passed to Graph endpoints like
 * `/users/{upn}/messages/{id}/$value`.
 */
const getRestMessageId = (itemId: string): string => {
  if (!itemId) return '';
  try {
    return Office.context.mailbox.convertToRestId(
      itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  } catch {
    return itemId;
  }
};

/**
 * Get conversation ID
 */
const getConversationId = (item: Office.MessageRead): Promise<string> => {
  return Promise.resolve(item.conversationId || '');
};

/**
 * Download all attachments from the email
 */
export const downloadAttachments = async (): Promise<AttachmentData[]> => {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item || !item.attachments || item.attachments.length === 0) {
      resolve([]);
      return;
    }

    const attachmentPromises = item.attachments.map((attachment) =>
      downloadSingleAttachment(attachment)
    );

    Promise.all(attachmentPromises)
      .then((attachments) => resolve(attachments.filter((a) => a !== null) as AttachmentData[]))
      .catch(reject);
  });
};

/**
 * Download a single attachment
 */
const downloadSingleAttachment = (attachment: Office.AttachmentDetails): Promise<AttachmentData | null> => {
  return new Promise((resolve) => {
    // Check if attachment is file type
    if (attachment.attachmentType !== Office.MailboxEnums.AttachmentType.File) {
      console.warn(`Skipping non-file attachment: ${attachment.name}`);
      resolve(null);
      return;
    }

    // Get attachment content
    Office.context.mailbox.item?.getAttachmentContentAsync(
      attachment.id,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const content = result.value.content;
          const format = result.value.format;

          resolve({
            filename: sanitizeFilename(attachment.name),
            originalFilename: attachment.name,
            fileSize: attachment.size,
            mimeType: attachment.contentType || 'application/octet-stream',
            content: format === Office.MailboxEnums.AttachmentContentFormat.Base64 
              ? content 
              : btoa(content),
          });
        } else {
          console.error(`Failed to download attachment: ${attachment.name}`, result.error);
          resolve(null);
        }
      }
    );
  });
};

/**
 * Sanitize filename for safe storage
 */
const sanitizeFilename = (filename: string): string => {
  // Remove or replace unsafe characters
  let sanitized = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  // Ensure unique filename with timestamp
  const timestamp = Date.now();
  const extension = filename.substring(filename.lastIndexOf('.'));
  const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.'));
  
  return `${nameWithoutExt}_${timestamp}${extension}`;
};

/**
 * Show notification in Outlook
 */
export const showNotification = (message: string, type: 'info' | 'error' | 'warning' = 'info'): void => {
  if (Office.context.mailbox.item?.notificationMessages) {
    const messageType = type === 'error' 
      ? Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage
      : Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage;

    Office.context.mailbox.item.notificationMessages.addAsync(
      'emailServiceNotification',
      {
        type: messageType,
        message,
        icon: 'icon-16',
        persistent: false,
      }
    );
  }
};

export default {
  extractEmailData,
  downloadAttachments,
  showNotification,
};
